import { useField } from 'formik';
import { KeyboardEvent } from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { Spinner } from '../../common/Spinner';
import { Label } from '../../common/Text';
import { CheckmarkIcon } from '../../icons';
import { palettes, tokens } from '../../theme/tokens';

const ContainerEnabled = css<{ checked?: boolean }>`
	color: ${tokens.text.inverse};
	cursor: pointer;

	${(props) =>
		props.checked
			? `
		background: ${tokens.text.primary};
		border-color: ${tokens.text.primary};

		&:hover,
		&[data-preview='hover'] {
			background: ${palettes.heatherGray['800']};
			border-color: ${palettes.heatherGray['800']};
		}
	`
			: `
		&:hover,
		&[data-preview='hover'] {
			border-color: ${tokens.border.focus};
		}
	`}

	&:focus-visible {
		outline: none;
		border-color: ${tokens.border.focus};
	}
`;

const ContainerDisabled = css`
	border-color: ${tokens.border.default};
	background: ${palettes.heatherGray['100']};
	color: ${tokens.text.disabled};
	cursor: not-allowed;
	pointer-events: none;
`;

const Container = styled(Box)<{ checked?: boolean; disabled?: boolean }>`
	width: 16px;
	height: 16px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.strong};
	border-radius: ${tokens.radius.xs}px;
	box-sizing: border-box;

	${(props) => (props.disabled ? ContainerDisabled : ContainerEnabled)}

	svg {
		display: block;
		width: 12px;
		height: 12px;
		color: inherit;
	}
`;

export function Checkbox(
	props: BoxProps & {
		checked?: boolean;
		label?: string;
		disabled?: boolean;
		onClick?: () => void;
		'data-preview'?: string;
	},
) {
	const { checked, disabled, label, onClick, 'data-preview': dataPreview, ...rest } = props;

	const handleClick = () => {
		if (!disabled) {
			onClick?.();
		}
	};

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleClick();
		}
	};

	return (
		<Flex alignItems="center" flexShrink={0}>
			<Container
				mr={label ? 2 : 0}
				flexShrink={0}
				{...rest}
				tabIndex={disabled ? -1 : 0}
				role="checkbox"
				aria-checked={!!checked}
				aria-disabled={disabled}
				checked={checked}
				disabled={disabled}
				data-preview={dataPreview}
				onClick={handleClick}
				onKeyDown={onKeyDown}
			>
				{checked && <CheckmarkIcon size={12} />}
			</Container>
			{label && (
				<Label my={2} onClick={handleClick}>
					{label}
				</Label>
			)}
		</Flex>
	);
}

function CheckboxFormik(props: BoxProps & { name: string; value?: string }) {
	const { name, value, ...boxProps } = props;
	const [{ value: inputValue }, , { setValue }] = useField<string[] | boolean>(name);

	const toggle = () => {
		if (typeof inputValue === 'boolean') {
			setValue(!inputValue);
		} else if (value) {
			const index = inputValue.indexOf(value);
			const updatedValue =
				index > -1 ? inputValue.filter((_, i) => i !== index) : [...inputValue, value];

			setValue(updatedValue);
		}
	};

	const isChecked =
		(Array.isArray(inputValue) && value && inputValue.includes(value)) ||
		(typeof inputValue === 'boolean' && inputValue);

	return <Checkbox checked={isChecked} onClick={toggle} {...boxProps} />;
}

Checkbox.Formik = CheckboxFormik;

Checkbox.Skeleton = function CheckboxSkeleton() {
	return (
		<Box p={1}>
			<Spinner />
		</Box>
	);
};
